import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, BACKEND_URL } from "@/lib/api";

export default function Archive() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [opts, setOpts] = useState({ regions: [], themes: [], communities: [] });
    const [filters, setFilters] = useState({ region: "", theme: "", community: "", q: "" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/archive/filters/options").then((r) => setOpts(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (filters.region) params.region = filters.region;
        if (filters.theme) params.theme = filters.theme;
        if (filters.community) params.community = filters.community;
        if (filters.q) params.q = filters.q;
        api.get("/archive", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
    }, [filters]);

    return (
        <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}>
            <Header />
            <section className="pt-28 pb-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                        <div>
                            <div className="overline mb-3">The archive</div>
                            <h1 className="font-display text-4xl lg:text-5xl">Verified photos, ready to license.</h1>
                            <p className="mt-3 max-w-2xl" style={{ color: "var(--gt-text-2)" }}>
                                Every image comes with a signed consent release and <strong style={{ color: "var(--gt-text)" }}>Content Provenance and Authenticity (C2PA)</strong> metadata —
                                an open standard that records where and when a photo was taken and verifies it has
                                not been altered. License instantly in USD for editorial, print, or campaign use.
                            </p>
                        </div>
                    </div>

                    <div className="gt-card p-5 mb-8 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="archive-filters">
                        <input
                            data-testid="archive-search"
                            className="gt-input"
                            placeholder="Search caption, title, community…"
                            value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                        />
                        <select data-testid="archive-filter-region" className="gt-input" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}>
                            <option value="">All Regions</option>
                            {opts.regions.map((r) => <option key={r}>{r}</option>)}
                        </select>
                        <select data-testid="archive-filter-theme" className="gt-input" value={filters.theme} onChange={(e) => setFilters({ ...filters, theme: e.target.value })}>
                            <option value="">All Themes</option>
                            {opts.themes.map((t) => <option key={t}>{t}</option>)}
                        </select>
                        <input
                            data-testid="archive-filter-community"
                            className="gt-input"
                            placeholder="Community (e.g., Sololá)"
                            value={filters.community}
                            onChange={(e) => setFilters({ ...filters, community: e.target.value })}
                        />
                    </div>

                    <div className="overline mb-4" data-testid="archive-count">
                        {loading ? "Loading…" : `${items.length} image${items.length === 1 ? "" : "s"}`}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.map((im) => (
                            <button
                                key={im.id}
                                data-testid={`archive-item-${im.id}`}
                                onClick={() => nav(`/archive/${im.id}`)}
                                className="gt-card overflow-hidden text-left group"
                            >
                                <div className="relative h-56 overflow-hidden">
                                    <img src={im.image_url || `${BACKEND_URL}/api/archive/image/${im.id}`} alt={im.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <span className="absolute top-3 left-3 meta-chip"><span className="verif-dot"></span> C2PA</span>
                                    <span className="absolute top-3 right-3 meta-chip" style={{ fontSize: 9 }}>{im.theme}</span>
                                </div>
                                <div className="p-5">
                                    <div className="overline mb-1" style={{ fontSize: 10 }}>{im.region} · {im.community || "—"}</div>
                                    <div className="font-display text-lg mb-2">{im.title}</div>
                                    <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>{im.caption}</p>
                                    <div className="mt-4 pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--gt-border)" }}>
                                        <span className="font-mono text-xs" style={{ color: "var(--gt-accent)" }}>From ${im.license_tiers?.[0]?.price || 150}</span>
                                        <span className="overline" style={{ fontSize: 10 }}>License →</span>
                                    </div>
                                </div>
                            </button>
                        ))}
                        {!loading && items.length === 0 && (
                            <div className="col-span-full text-center py-16" style={{ color: "var(--gt-text-2)" }}>
                                No images match these filters.
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
